import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { products } from '@/utils/api';
import { Search, Heart } from 'lucide-react';
import { toast } from 'sonner';

const Products = () => {
  const [productList, setProductList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [likedProducts, setLikedProducts] = useState(new Set());

  const categories = [
    'Paintings',
    'Sculptures',
    'Photography',
    'Digital Art',
    'Crafts',
    'Pottery',
    'Textiles',
    'Jewelry',
    'Other'
  ];

  useEffect(() => {
    loadProducts();
    // Load liked products from localStorage
    const saved = localStorage.getItem('likedProducts');
    if (saved) {
      setLikedProducts(new Set(JSON.parse(saved)));
    }
  }, [category]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (category && category !== 'all') params.category = category;
      const response = await products.getAll(params);
      setProductList(response.data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadProductsWithSearch();
  };

  const handleLike = async (e, productId) => {
    e.preventDefault(); // Prevent navigation
    e.stopPropagation();
    
    // Check if already liked
    if (likedProducts.has(productId)) {
      toast.info('You already loved this artwork!');
      return;
    }
    
    try {
      const response = await products.like(productId);
      
      // Update local state
      const newLiked = new Set(likedProducts);
      newLiked.add(productId);
      setLikedProducts(newLiked);
      localStorage.setItem('likedProducts', JSON.stringify([...newLiked]));
      
      // Update product likes in state
      setProductList(prev => prev.map(p => 
        p.id === productId ? {...p, likes: response.data.likes} : p
      ));
      
      toast.success('Thank you for your appreciation!');
    } catch (error) {
      toast.error('Could not register your love. Try again!');
    }
  };

  const loadProductsWithSearch = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (category && category !== 'all') params.category = category;
      const response = await products.getAll(params);
      setProductList(response.data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = searchTerm
    ? productList.filter(p =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : productList;

  return (
    <div data-testid="products-page">
      <SEO 
        title="Browse Artworks"
        description="Explore our curated collection of unique handcrafted artworks. Find paintings, sculptures, pottery, jewelry and more from talented independent artists."
        keywords="buy art, paintings for sale, sculptures, handmade crafts, art gallery, contemporary art, Indian art"
        url="/products"
      />
      <Navbar />
      
      <div style={{paddingTop: "180px"}}>
        <section className="section">
          <h1 className="section-title" data-testid="products-title">Explore Artworks</h1>
          <p className="section-subtitle" data-testid="products-subtitle">
            Browse our collection of unique handcrafted pieces from talented artists
          </p>

          <div className="max-w-4xl mx-auto mb-8">
            <div className="flex gap-4 mb-4" data-testid="search-filters">
              <div className="flex-1 flex gap-2">
                <Input
                  placeholder="Search artworks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="flex-1"
                  data-testid="search-input"
                />
                <Button onClick={handleSearch} data-testid="search-btn">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              <Select value={category || undefined} onValueChange={setCategory}>
                <SelectTrigger className="w-[200px]" data-testid="category-select">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12" data-testid="loading-spinner">
              <p className="text-gray-500">Loading products...</p>
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="product-grid" data-testid="products-grid">
              {filteredProducts.map((product) => (
                <div key={product.id} className="product-card">
                  <Link
                    to={`/products/${product.id}`}
                    data-testid={`product-card-${product.id}`}
                  >
                    <img
                      src={products.getImage(product.images[0])}
                      alt={product.title}
                      className="product-image"
                    />
                    <div className="product-info">
                      <h3 className="product-title">{product.title}</h3>
                      <p className="text-sm text-gray-600 mb-1">by {product.artistName}</p>
                      <p className="text-sm text-gray-500 mb-2">{product.category}</p>
                      <p className="product-price">₹{product.price.toLocaleString()}</p>
                    </div>
                  </Link>
                  {/* Heart Button */}
                  <button
                    onClick={(e) => handleLike(e, product.id)}
                    className={`w-full mt-3 py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 ${
                      likedProducts.has(product.id)
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-100 hover:bg-red-100 text-gray-700 hover:text-red-500'
                    }`}
                  >
                    <Heart 
                      className={`w-5 h-5 ${likedProducts.has(product.id) ? 'fill-white' : ''}`} 
                    />
                    <span className={`ml-1 px-2 py-0.5 rounded-full text-sm ${
                      likedProducts.has(product.id) 
                        ? 'bg-white/20' 
                        : 'bg-red-100 text-red-600'
                    }`}>
                      {product.likes || 0}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12" data-testid="no-products">
              <p className="text-gray-500 text-lg">No products found</p>
              <p className="text-gray-400 mt-2">Try adjusting your search or filters</p>
            </div>
          )}
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default Products;